import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Warning, XCircle, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LIGHT = {
  green: { dot: "bg-emerald-500", ring: "ring-emerald-500/20", Icon: CheckCircle, text: "text-emerald-600" },
  amber: { dot: "bg-amber-500", ring: "ring-amber-500/20", Icon: Warning, text: "text-amber-600" },
  red: { dot: "bg-red-500", ring: "ring-red-500/20", Icon: XCircle, text: "text-red-600" },
};

function sevLight(sev) {
  return sev === "ok" ? "green" : sev === "critical" ? "red" : "amber";
}

function StatusCard({ item }) {
  const light = LIGHT[sevLight(item.severity)];
  const Icon = light.Icon;
  return (
    <div
      data-testid={`sec-card-${item.label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`rounded-lg border border-border bg-card p-4 ring-1 ${light.ring}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
          <p className="mt-1 truncate text-sm font-semibold text-foreground">{item.value}</p>
          {item.host && <p className="mt-0.5 truncate text-xs text-muted-foreground">host: {item.host}</p>}
          {item.note && <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>}
        </div>
        <Icon size={20} weight="fill" className={`shrink-0 ${light.text}`} />
      </div>
    </div>
  );
}

export default function SecuritySettings() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [dash, setDash] = useState(null);
  const [account, setAccount] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  // forms
  const [emailPw, setEmailPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a, i] = await Promise.all([
        api.get("/admin/security/dashboard"),
        api.get("/admin/security/account"),
        api.get("/admin/security/incidents"),
      ]);
      setDash(d.data);
      setAccount(a.data);
      setIncidents(i.data.incidents || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    try {
      await api.post("/admin/security/scan");
      toast.success("Scan complete");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const ackIncident = async (id) => {
    try {
      await api.post(`/admin/security/incidents/${id}/acknowledge`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/change-email", {
        current_password: emailPw, new_email: newEmail,
      });
      toast.success(data.message);
      setEmailPw(""); setNewEmail("");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/change-password", {
        current_password: curPw, new_password: newPw, confirm_password: confirmPw,
      });
      toast.success(data.message);
      // Password change signs out ALL sessions → log the user out client-side.
      setTimeout(() => logout(), 1200);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!user?.is_platform_admin) {
    return <div className="p-8" data-testid="security-denied">Platform administrator access required.</div>;
  }

  const overall = dash ? LIGHT[dash.overall_light] : null;

  return (
    <div className="max-w-5xl" data-testid="security-page">
      <PageHeader
        title={t("security.title")}
        subtitle={t("security.subtitle")}
        action={
          <div className="flex items-center gap-2">
            {dash && (
              <span
                data-testid="security-overall"
                className={`inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs font-medium`}
              >
                <span className={`h-2.5 w-2.5 rounded-full ${overall.dot}`} />
                {dash.overall_severity === "ok" ? "Secure" : dash.overall_severity === "critical" ? "Critical" : "Warning"}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={load} data-testid="security-refresh">
              <ArrowsClockwise size={15} className="mr-1.5" /> {t("security.refresh")}
            </Button>
          </div>
        }
      />

      {/* Dashboard */}
      <section className="mb-10">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-base font-semibold text-foreground">{t("security.dashboardTitle")}</h2>
          <Button variant="ghost" size="sm" onClick={runScan} data-testid="security-run-scan">
            <ShieldCheck size={15} className="mr-1.5" /> {t("security.runScan")}
          </Button>
        </div>
        {loading && !dash ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : dash ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(dash.items).map(([k, item]) => <StatusCard key={k} item={item} />)}
          </div>
        ) : null}
      </section>

      {/* Incidents */}
      <section className="mb-10">
        <h2 className="mb-3 font-heading text-base font-semibold text-foreground">{t("security.incidentsTitle")}</h2>
        {incidents.length === 0 ? (
          <div data-testid="security-no-incidents" className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
            {t("security.noIncidents")}
          </div>
        ) : (
          <div className="space-y-2" data-testid="security-incidents-list">
            {incidents.map((inc) => (
              <div key={inc.id} data-testid={`incident-${inc.id}`}
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${inc.severity === "critical" ? "bg-red-500" : "bg-amber-500"}`} />
                    <p className="text-sm font-semibold text-foreground">{inc.title}</p>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{inc.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{inc.detail}</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span data-testid={`incident-count-${inc.id}`}>Detection count: <span className="font-semibold text-foreground">{inc.detection_count ?? 1}</span></span>
                    <span>First seen: {inc.first_seen}</span>
                    <span>Last seen: {inc.last_seen}</span>
                  </div>
                </div>
                {inc.status === "open" && (
                  <Button variant="outline" size="sm" onClick={() => ackIncident(inc.id)}
                    data-testid={`ack-${inc.id}`}>{t("security.acknowledge")}</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Account management */}
      <section>
        <h2 className="mb-3 font-heading text-base font-semibold text-foreground">{t("security.accountTitle")}</h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Change email */}
          <form onSubmit={submitEmail} className="rounded-lg border border-border bg-card p-5 space-y-4" data-testid="change-email-form">
            <div>
              <p className="text-sm font-semibold text-foreground">{t("security.changeEmail")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("security.currentEmail")}: <span className="font-mono" data-testid="current-admin-email">{account?.email || "—"}</span></p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("security.newEmail")}</Label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required
                data-testid="new-email-input" placeholder="new-admin@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("security.currentPassword")}</Label>
              <Input type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} required
                data-testid="email-current-password-input" autoComplete="current-password" />
            </div>
            <Button type="submit" disabled={busy} data-testid="change-email-submit">{t("security.changeEmail")}</Button>
          </form>

          {/* Change password */}
          <form onSubmit={submitPassword} className="rounded-lg border border-border bg-card p-5 space-y-4" data-testid="change-password-form">
            <div>
              <p className="text-sm font-semibold text-foreground">{t("security.changePassword")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("security.pwHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("security.currentPassword")}</Label>
              <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required
                data-testid="pw-current-input" autoComplete="current-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("security.newPassword")}</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required
                data-testid="pw-new-input" autoComplete="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("security.confirmPassword")}</Label>
              <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required
                data-testid="pw-confirm-input" autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={busy} data-testid="change-password-submit">{t("security.changePassword")}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
